import {
	HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse
} from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, ignoreElements, map, share, switchMap, tap } from 'rxjs/operators';
import { IRESTConfig, RESTConfig } from './RESTConfig';

import { concat } from 'rxjs/observable/concat';
import { empty } from 'rxjs/observable/empty';
import { from } from 'rxjs/observable/from';
import { _throw as throwError } from 'rxjs/observable/throw';
import { ITokenStore, TokenStore } from './ITokenStore';
import { Router } from '@angular/router';

/**
 * Definizione di utente. Una semplice tupla (codice_fiscale, pin?, password).
 *
 * @export
 */
export interface IUser {
	codice_fiscale: string;
	pin?: string;
	password: string;
}

/**
 * Definizione di richiesta per il reset della password. La tupla dell'utente
 * potrebbe essere sostituita da un token, nel caso di reset per email (non
 * ancora implementato).
 *
 * @export
 */
export interface IResetRequest {
	codice_fiscale: string;
	pin?: string;
	password_vecchia: string;
	password_nuova: string;
}

/**
 * Rappresenta lo stato del login dell'utente.
 *
 * @export
 * @enum {number}
 */
export enum OAuth2Status {
	Unauthorized,
	Pending,
	Logged
}

/**
 * Servizio adibito all'autenticazione (e autorizzazione)
 * dell'utente. Fornisce un interceptor per arricchire le
 * chiamate HTTP con delle funzionalità aggiuntive come token
 * refresh.
 *
 * Permette anche di ottenere lo stato del login, di
 * effettuare il login e sincronizzarsi con la sua
 * riuscita/il suo fallimento, di effettuare il logout e
 * di effettuare un refresh manuale del token.
 *
 * @export
 */
@Injectable()
export class OAuth2Service {
	public readonly statusChanged: Observable<OAuth2Status>;

	public lastResponse = {}

	private _status: OAuth2Status;
	private authPromise: Observable<any>;
	private statusSubject = new Subject<OAuth2Status>();
	private client: HttpClient;

	/**
	 * Restituisce lo stato attuale del login
	 *
	 * @readonly
	 * @memberof OAuth2Service
	 */
	get status() {
		return this._status;
	}

	constructor(
		private injector: Injector,
		private router: Router,
		@Inject(RESTConfig) private config: IRESTConfig,
		@Inject(TokenStore) private tokenStore: ITokenStore
	) {

		this._status = OAuth2Status.Logged;
		this.statusChanged = this.statusSubject.asObservable().pipe(
			distinctUntilChanged(),
			tap(status => this.tokenStore.statusChanged(status))
		);
	}

	/**
	 * @ignore
	 *
	 */
	/*intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		this.client = this.client || this.injector.get(HttpClient);
		switch (this._status) {
			case OAuth2Status.Pending:
				if (!req.withCredentials) {
					return concat(
						this.authPromise,
						// FIXME: non mi piace molto...
						from([true]).pipe(
							switchMap(() => next.handle(this.tokenStore.setupRequest(req))),
							catchError((error: HttpErrorResponse) => {
								if (error.status === 401) {
									this.refresh();
									return this.intercept(req, next);
								} else {
									return throwError(error);
								}
							})
						)
					) as Observable<HttpEvent<any>>;
				} else {
					return next.handle(req);
				}
			case OAuth2Status.Logged:
				// Questo per evitare di gestire richieste autenticate
				// al di fuori di questo servizio.
				if (!req.withCredentials) {
					return next.handle(this.tokenStore.setupRequest(req))
						.pipe(
							catchError((error: HttpErrorResponse) => {
								if (error.status === 401) {
									this.refresh();
									return this.intercept(req, next);
								} else {
									return throwError(error);
								}
							})
						);
				} else {
					return next.handle(req);
				}
			default:
				return next.handle(req);
		}

	}*/

	/**
	 * Effettua il login per l'utente passato come unico argomento.
	 *
	 * @returns {Observable<void>} Restituisce un observable per tracciare
	 * la riuscita o il fallimento della richiesta.
	 * @memberof OAuth2Service
	 */
	login(user: IUser | any, action = 'password'): Observable<void> {
		this.client = this.client || this.injector.get(HttpClient);
		//if (this._status !== OAuth2Status.Pending) {
		this._status = OAuth2Status.Pending;
		return this.authPromise = this.client.post(
			`${this.config.baseURL}/${this.config.oauthNode}`,
			Object.assign({ grant_type: action }, user),
			{
				withCredentials: true,
				observe: 'response',
				responseType: 'text'
			}
		).pipe(
			map((r) => {
				this.tokenStore.tokenUpdated(r);
				this.lastResponse = r
			}),
			tap(
				() => {
					this._status = OAuth2Status.Logged;
					this.statusSubject.next(OAuth2Status.Logged);
				},
				(err: HttpErrorResponse) => {
					if (err.status === 403) {
						this._status = OAuth2Status.Unauthorized;
						this.statusSubject.next(OAuth2Status.Unauthorized);
					}
				}
			),
			ignoreElements(),
			share()
		);
		/* } else {
			return empty();
		} */
	}

	/**
	 * Forza il refresh del token.
	 *
	 * @returns Restituisce un observable per tracciare
	 * la riuscita o il fallimento della richiesta.
	 * @memberof OAuth2Service
	 */
	refresh() {
		this.logout()
		return

		this.client = this.client || this.injector.get(HttpClient);

		// Non posso fare refresh se non sono loggato
		if (this._status !== OAuth2Status.Unauthorized) {
			// FIXME: c'è una corsa critica evidente se tengo aperte
			// almeno due pagine ed entrambe fanno refresh. Come gestisco?
			// [ In pratica poi non dovrebbe succedere dato che l'unica richiesta
			// 	 che continua ad essere fatta/ripetuta/tenuta aperta è il websocket ]

			// Non duplico le richieste. Punto singolo di attesa.
			if (this._status !== OAuth2Status.Pending) {
				this._status = OAuth2Status.Pending;
				return this.authPromise = this.tokenStore.refreshRequest(this.client).pipe(
					map((r) => { this.tokenStore.tokenUpdated(r); }),
					tap(
						() => {
							this._status = OAuth2Status.Logged;
							this.statusSubject.next(OAuth2Status.Logged);
						},
						(err: HttpErrorResponse) => {
							if (err.status === 403) {
								this._status = OAuth2Status.Unauthorized;
								this.statusSubject.next(OAuth2Status.Unauthorized);
							}
						}
					),
					ignoreElements(),
					share()
				);
			} else {
				return this.authPromise;
			}
		} else {
			return throwError(new Error(`Invalid state-machine status (${this._status}).`));
		}
	}

	logout(): void {
		this.client = this.client || this.injector.get(HttpClient);
		this._status = OAuth2Status.Unauthorized;
		this.tokenStore.logout(this.router);
		this.statusSubject.next(OAuth2Status.Unauthorized);
	}

	async reset_password(request: IResetRequest) {
		const req = await fetch(`${this.config.baseURL}/reset`, {
			method: 'POST',
			body: JSON.stringify(request),
			headers: {
				"Content-Type": "application/json",
			}
		})

		return await req.text()

		/*this.client = this.client || this.injector.get(HttpClient);
		return this.client.post(
			`${this.config.baseURL}/reset`,
			request,
			{
				// PORK: è in corso per questo use-case l'implementazione ufficiale
				// nel frattempo abuso a porco degli headers.
				withCredentials: false,
				// observe: 'response',
				responseType: 'text'
			}
		);*/
	}
}



// WEBPACK FOOTER //
// ./src/app/angular-rest/OAuth2Service.ts